import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Header from './Header';
import { mapNamesToWidths } from '../utils/mapNamesToWidths';
import TableBody from './TableBody';
import { getTableMinWidth, makeGetColumnWidth, makeGetFrozenOffsets, setAllWidths } from '../../resizing/redux/widthsSlice';
import { ProvideObserver } from '../../virtualization/context/observerContext';
import { useWidthsDispatch, useWidthsSelector, useSelectionSelector, useSelectionDispatch } from '../../redux/reduxHooks';
import { setRowRegistry } from '../redux/rowRegistrySlice';
import SelectAllCheckboxHeader from '../../selection/components/SelectAllCheckboxHeader';
import TableTopBar from './TableTopBar';
import { CUSTOM_TABLE_HEADER_HEIGHT, SELECTION_COLUMN_WIDTH, TABLE_COLLAPSED_TOOLBAR_WIDTH, TABLE_PAGINATOR_HEIGHT, TABLE_RIGHT_TOOLBAR_WIDTH, TABLE_TOP_BAR_HEIGHT } from '../constants/sizes';
import { Box } from 'foundations-components/transitional/components/Box';
import Paginator from '../../pagination/components/Paginator';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { TableScrollContainer, TableBox, NoFirstDivChildPadding } from './StyledTableComponents';
import { getColumnKey } from '../utils/getColumnKey';
import { useWatchTableContainer } from '../hooks/useWatchTableContainer';
import { getShouldShowBulkActionsContainer } from '../../selection/redux/selectionSlice';
import SelectQueryRow from '../../selection/components/SelectQueryRow';
import { Flex } from 'foundations-components/components/Flex';
import { useShowSelectQuery } from '../hooks/useShowSelectQuery';
import { useFrozenColumnWidthAlert } from '../hooks/useFrozenColumnWidthAlert';
import { infiniteScrollDisabled, isInfiniteScrollEnabled } from '../../infiniteScroll/config';
import { useInfiniteScroll } from '../../infiniteScroll/useInfiniteScroll';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ColumnCallToActionOverlay } from '../columnCallToAction/ColumnCallToActionOverlay';
import { CellActionMenuOverlay } from '../../cellSelection/components/CellActionMenuOverlay';
import { useIsCellSelectionEnabled } from '../../cellSelection/context/CellSelectionContext';
import { getColumnCount } from '../utils/getColumnCount';
import { ProvideInfiniteScroll } from '../context/InfiniteScrollContext';
import { isRightToolbarEnabled, rightToolbarDisabled } from '../../rightToolbar/config';
import RightToolbarRail from '../../rightToolbar/RightToolbarRail';

// Extra specificity is necessary to override the default height
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTR = styled.tr.withConfig({
  displayName: "Table__StyledTR"
})(["&& th{height:", ";}"], CUSTOM_TABLE_HEADER_HEIGHT);
const Table = ({
  alternateHeaderLeftSlot,
  BulkActionsComponent,
  canSelectAllRecords,
  columns,
  condensed,
  dataDensity,
  CustomSelectAllComponent,
  disableScrollReset,
  getRowId,
  headerLeftSlot,
  headerRightSlot,
  infiniteScrollConfig = infiniteScrollDisabled,
  isBulkActionsRedesignEnabled,
  isIndexRedesignEnabled,
  loading,
  noRowsSlot,
  numberOfFrozenColumns,
  onColumnOrderChange,
  onColumnWidthChange,
  onPageIndexChange,
  onPageSizeChange,
  onSortsChange,
  pageable: showPagination,
  pageIndex,
  pageSize,
  pageSizes,
  paginatorAlignment,
  RowComponent,
  rows,
  scrollContainerRef,
  selectable,
  sorts,
  TableComponent,
  totalRowCount,
  zebraStripe,
  enableFullVirtualization,
  onFrozenColumnWidthAlert,
  hasTopBarBorderRadius,
  rightToolbarConfig = rightToolbarDisabled
}) => {
  const rowIds = useMemo(() => rows.map(getRowId), [getRowId, rows]);
  const cellSelectionEnabled = useIsCellSelectionEnabled();
  const {
    observer,
    handleTableContainerRef
  } = useWatchTableContainer({
    rowIds,
    disableScrollReset,
    scrollContainerRef
  });
  const scrollContainerElementRef = useRef(null);
  const [scrollContainerElement, setScrollContainerElement] = useState(null);
  const handleScrollContainerRef = useCallback(element => {
    scrollContainerElementRef.current = element;
    setScrollContainerElement(element);
    handleTableContainerRef(element);
  }, [handleTableContainerRef]);
  const {
    ScrollToTopButton
  } = useInfiniteScroll(scrollContainerElement, infiniteScrollConfig);
  const enabledRightToolbarConfig = isRightToolbarEnabled(rightToolbarConfig) ? rightToolbarConfig : undefined;
  const isToolbarPinnable = (enabledRightToolbarConfig === null || enabledRightToolbarConfig === void 0 ? void 0 : enabledRightToolbarConfig.isPinned) !== undefined;
  const isToolbarPinned = isToolbarPinnable ? enabledRightToolbarConfig === null || enabledRightToolbarConfig === void 0 ? void 0 : enabledRightToolbarConfig.isPinned : true;
  const onPinnedChange = enabledRightToolbarConfig === null || enabledRightToolbarConfig === void 0 ? void 0 : enabledRightToolbarConfig.onPinnedChange;
  const handleToggleToolbarPin = onPinnedChange ? () => onPinnedChange(!isToolbarPinned) : undefined;
  const toolbarWidth = enabledRightToolbarConfig ? !isToolbarPinnable || isToolbarPinned ? TABLE_RIGHT_TOOLBAR_WIDTH : TABLE_COLLAPSED_TOOLBAR_WIDTH : 0;
  const columnCount = getColumnCount(columns.length, selectable);
  const initializedWidths = useRef(false);
  const widthsDispatch = useWidthsDispatch();
  const selectionDispatch = useSelectionDispatch();
  useLayoutEffect(() => {
    // We only need to set columns widths if the columns changed
    if (!initializedWidths.current) {
      initializedWidths.current = true;
      return;
    }
    widthsDispatch(setAllWidths(mapNamesToWidths(columns)));
  }, [columns, widthsDispatch]);
  useLayoutEffect(() => {
    // Update the row registry when rowIds change
    selectionDispatch(setRowRegistry(rowIds));
  }, [selectionDispatch, rowIds]);
  const sortMap = useMemo(() => sorts.reduce((acc, {
    columnNamespace,
    columnName,
    direction
  }) => {
    acc[getColumnKey(columnNamespace, columnName)] = direction;
    return acc;
  }, {}), [sorts]);
  const tableMinWidth = useWidthsSelector(getTableMinWidth) + (selectable ? SELECTION_COLUMN_WIDTH : 0);

  // TODO: Add multisort when the index page can handle it
  const handleSortChange = useCallback(({
    columnNamespace: columnNamespace_0,
    columnName: columnName_0,
    direction: direction_0
  }) => {
    onSortsChange({
      nextSorts: [{
        columnNamespace: columnNamespace_0,
        columnName: columnName_0,
        direction: direction_0
      }]
    });
  }, [onSortsChange]);
  const {
    showSelectQueryInBulkActionsContainer,
    showSelectQueryRow
  } = useShowSelectQuery(rows, getRowId, canSelectAllRecords, isBulkActionsRedesignEnabled, totalRowCount);
  const shouldShowBulkActionsInTopBar = useSelectionSelector(getShouldShowBulkActionsContainer) && (!isBulkActionsRedesignEnabled || !isIndexRedesignEnabled);
  const hasHeaderSlots = Boolean(headerLeftSlot || headerRightSlot);
  const showTopBar = shouldShowBulkActionsInTopBar || hasHeaderSlots;
  const tableBoxHasTopBorderRadius = !showTopBar;
  const infiniteScrollEnabled = isInfiniteScrollEnabled(infiniteScrollConfig);
  const isMaxRowsExceeded = infiniteScrollEnabled && infiniteScrollConfig.isMaxRowsExceeded;
  const showTopSentinel = infiniteScrollEnabled && infiniteScrollConfig.hasPreviousCursor;
  const showBottomSentinel = infiniteScrollEnabled && !isMaxRowsExceeded && infiniteScrollConfig.hasNextCursor;

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- The linter flags this because it can't prove the compiler will preserve the useMemo semantics for the selector factory returned by createSelector (which has its own internal memoization cache). It's actually OK because even if the compiler re-creates the selector, useWidthsSelector will still return the correct value — only the selector's internal cache is lost, which is a perf-only concern, not a correctness one.
  const getLastColumnWidth = useMemo(() => {
    // HACK: Some consumers will pass an empty columns array. In this case we'll
    // just return 0 since without columns the table doesn't do anything interesting anyway.
    const lastColumn = columns[columns.length - 1];
    if (!lastColumn) {
      return () => 0;
    }
    return makeGetColumnWidth(getColumnKey(lastColumn.columnNamespace, lastColumn.columnName));
  }, [columns]);
  const lastColumnWidth = useWidthsSelector(getLastColumnWidth);
  const requiredTableProps = useMemo(() => ({
    // HACK: This is required for compat with onboarding tours. Once
    // we have renamed the contact "name" column, we can PR their library
    // to use the actual external id selector and remove this one.
    'data-onboarding': 'grid-table',
    'data-table-external-id': 'framework-data-table',
    'data-test-id': 'framework-data-table',
    'data-test-loading': loading,
    lastColumnWidth,
    minWidth: tableMinWidth
  }), [loading, tableMinWidth, lastColumnWidth]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Same rationale as getLastColumnWidth above.
  const getFrozenOffsets = useMemo(() => {
    if (numberOfFrozenColumns === 0) {
      return () => [];
    }
    const columnKeys = columns.map(column => {
      return getColumnKey(column.columnNamespace, column.columnName);
    }).slice(0, numberOfFrozenColumns - 1);
    return makeGetFrozenOffsets(columnKeys, selectable);
  }, [columns, numberOfFrozenColumns, selectable]);
  const frozenOffsets = useWidthsSelector(getFrozenOffsets);
  useFrozenColumnWidthAlert(columns, frozenOffsets, scrollContainerRef, onFrozenColumnWidthAlert);
  const thElements = useMemo(() => {
    return columns.map((column_0, index) => {
      const key = getColumnKey(column_0.columnNamespace, column_0.columnName);
      return /*#__PURE__*/_jsx(Header, {
        column: column_0,
        columnIndex: index,
        columnPosition: frozenOffsets[index],
        isLastColumn: index === columns.length - 1,
        sortDirection: sortMap[key],
        onSortChange: handleSortChange,
        onColumnWidthChange: onColumnWidthChange,
        onColumnOrderChange: onColumnOrderChange,
        numberOfFrozenColumns: numberOfFrozenColumns
      }, key);
    });
  }, [columns, frozenOffsets, handleSortChange, numberOfFrozenColumns, onColumnOrderChange, onColumnWidthChange, sortMap]);
  return /*#__PURE__*/_jsxs(Flex, {
    blockSize: "100%",
    "data-test-id": "framework-data-table-container",
    flexDirection: "column",
    inlineSize: "100%",
    overflow: "hidden",
    children: [showTopBar && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_TOP_BAR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(TableTopBar, {
        alternateHeaderLeftSlot: alternateHeaderLeftSlot,
        BulkActionsComponent: BulkActionsComponent,
        headerLeftSlot: headerLeftSlot,
        headerRightSlot: headerRightSlot,
        isBulkActionsRedesignEnabled: isBulkActionsRedesignEnabled,
        isIndexRedesignEnabled: isIndexRedesignEnabled,
        showSelectQuery: showSelectQueryInBulkActionsContainer,
        totalRowCount: totalRowCount,
        hasTopBarBorderRadius: hasTopBarBorderRadius
      })
    }), /*#__PURE__*/_jsxs(TableBox, {
      showPagination: showPagination,
      showTopBar: showTopBar,
      loading: loading,
      hasTopBorderRadius: tableBoxHasTopBorderRadius,
      children: [loading && /*#__PURE__*/_jsx(NoFirstDivChildPadding, {
        children: /*#__PURE__*/_jsx(LoadingOverlay, {
          "data-test-id": "table-loading",
          contextual: true
        })
      }), enabledRightToolbarConfig && rows.length > 0 && /*#__PURE__*/_jsx(RightToolbarRail, {
        config: enabledRightToolbarConfig,
        width: toolbarWidth,
        headerHeight: CUSTOM_TABLE_HEADER_HEIGHT,
        isPinned: isToolbarPinned,
        onTogglePin: handleToggleToolbarPin,
        isToolbarPinnable: isToolbarPinnable
      }), rows.length > 0 ? /*#__PURE__*/_jsxs(TableScrollContainer, {
        ref: handleScrollContainerRef,
        rightToolbarWidth: enabledRightToolbarConfig ? isToolbarPinned ? TABLE_RIGHT_TOOLBAR_WIDTH : TABLE_COLLAPSED_TOOLBAR_WIDTH : undefined,
        children: [/*#__PURE__*/_jsx(ProvideInfiniteScroll, {
          config: infiniteScrollConfig,
          children: /*#__PURE__*/_jsx(ProvideObserver, {
            value: observer,
            children: /*#__PURE__*/_jsxs(TableComponent, {
              numberOfFrozenColumns: numberOfFrozenColumns,
              frozenOffsets: frozenOffsets,
              requiredTableProps: requiredTableProps,
              zebraStripe: zebraStripe,
              condensed: condensed,
              dataDensity: dataDensity,
              children: [/*#__PURE__*/_jsxs("thead", {
                children: [/*#__PURE__*/_jsxs(StyledTR, {
                  children: [selectable && /*#__PURE__*/_jsx(SelectAllCheckboxHeader, {
                    rowIds: rowIds
                  }), thElements]
                }), showSelectQueryRow && /*#__PURE__*/_jsx(SelectQueryRow, {
                  rowIds: rowIds,
                  totalRowCount: totalRowCount,
                  columnCount: columnCount,
                  CustomSelectAllComponent: CustomSelectAllComponent
                })]
              }), showTopSentinel && /*#__PURE__*/_jsx("tbody", {
                style: {
                  overflowAnchor: 'none'
                },
                "aria-hidden": "true",
                children: /*#__PURE__*/_jsx("tr", {
                  "data-test-id": "infinite-scroll-top-sentinel",
                  children: /*#__PURE__*/_jsx("td", {
                    style: {
                      border: 'none',
                      blockSize: '1px',
                      padding: 0
                    }
                  })
                })
              }), /*#__PURE__*/_jsx(TableBody, {
                columns: columns,
                dataDensity: dataDensity,
                getRowId: getRowId,
                numberOfFrozenColumns: numberOfFrozenColumns,
                pageable: showPagination,
                pageSize: pageSize,
                RowComponent: RowComponent,
                rows: rows,
                selectable: selectable,
                enableFullVirtualization: enableFullVirtualization
              }), showBottomSentinel && /*#__PURE__*/_jsx("tbody", {
                style: {
                  overflowAnchor: 'none'
                },
                "aria-hidden": "true",
                children: /*#__PURE__*/_jsx("tr", {
                  "data-test-id": "infinite-scroll-bottom-sentinel",
                  children: /*#__PURE__*/_jsx("td", {
                    style: {
                      border: 'none',
                      blockSize: '1px',
                      padding: 0
                    }
                  })
                })
              }), isMaxRowsExceeded && /*#__PURE__*/_jsx("tbody", {
                children: /*#__PURE__*/_jsx("tr", {
                  children: /*#__PURE__*/_jsx("td", {
                    colSpan: columnCount,
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "datatable.pagination.tooManyRecords"
                    })
                  })
                })
              })]
            })
          })
        }), /*#__PURE__*/_jsx(ColumnCallToActionOverlay, {
          numberOfFrozenColumns: numberOfFrozenColumns,
          scrollContainerElementRef: scrollContainerElementRef
        }), cellSelectionEnabled && /*#__PURE__*/_jsx(CellActionMenuOverlay, {
          scrollContainerElementRef: scrollContainerElementRef,
          columns: columns,
          rows: rows,
          getRowId: getRowId
        })]
      }) : noRowsSlot, ScrollToTopButton]
    }), showPagination && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_PAGINATOR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(Paginator, {
        pageIndex: pageIndex,
        pageSize: pageSize,
        pageSizes: pageSizes,
        paginatorAlignment: paginatorAlignment,
        totalRowCount: totalRowCount,
        onPageIndexChange: onPageIndexChange,
        onPageSizeChange: onPageSizeChange
      })
    })]
  });
};
export default Table;